import { FC, HTMLProps, ReactNode } from 'react'

import useHover from 'src/hooks/useHover'
import { cn } from 'src/lib/cn'
import HtmlProps from 'src/types/HtmlProps'
import ComponentColor from 'src/types/ItemColor'
import componentColorVariants from '../../variants/componentColorVariants'
import BadgeAddOn from '../BadgeAddOn'

export interface DropdownMenuItemProps
  extends Omit<HtmlProps<HTMLDivElement>, 'label' | 'selected'> {
  icon?: ReactNode
  label: ReactNode
  active?: boolean
  rightIcon?: ReactNode
  disabled?: boolean
  componentColor?: ComponentColor
  withoutBorder?: boolean
  classes?: {
    label?: string
    hoverLabel?: string
    icon?: string
    hoverIcon?: string
    activeIcon?: string
    rightIcon?: string
    hoverRightIcon?: string
    addon?: string
  }
  href?: string
  height?: number
  addon?: string
  isHovered?: boolean
}

const DropdownMenuItem: FC<DropdownMenuItemProps> = ({
  icon,
  rightIcon,
  label,
  disabled,
  className,
  active,
  onClick,
  componentColor: componentColorProp,
  withoutBorder,
  classes,
  onMouseEnter,
  onMouseLeave,
  height,
  style,
  addon,
  isHovered: isHoveredProp,
  ...divProps
}) => {
  const { hoverProps, isHovered: isHoveredState } = useHover({
    onMouseMove: onMouseEnter,
    onMouseLeave,
  })

  const componentColor = componentColorProp ?? ComponentColor.blue

  const isHovered = isHoveredProp ?? isHoveredState

  const Component = (divProps.href ? 'a' : 'div') as any as FC<HTMLProps<HTMLDivElement>>

  return (
    <Component
      className={cn(
        'text-transform-none text-shuttle-gray flex h-[33px] w-full cursor-pointer flex-nowrap items-center gap-2.5 text-sm font-normal',
        'p-[0px_16px_0px_11.8px]',
        disabled && 'pointer-events-none bg-[unset]! font-normal! opacity-50 grayscale-[100%]',
        isHovered &&
          cn(
            componentColorVariants({
              rowHover: componentColor,
            }),
            'text-mine-shaft font-semibold',
          ),
        isHovered && classes?.hoverLabel,
        active &&
          cn(
            componentColorVariants({
              dropdownBorder: componentColor,
              background: componentColor,
            }),
            'font-semibold',
            withoutBorder && 'border-l-0',
          ),
        className,
      )}
      onClick={disabled ? undefined : onClick}
      role="menuitem"
      data-active={active || undefined}
      aria-current={active ? 'true' : undefined}
      style={{
        ...style,
        height,
      }}
      {...hoverProps}
      {...divProps}
    >
      {icon && (
        <div
          data-testid="dropdown-menu-item-icon"
          className={cn(
            'flex h-full items-center',
            classes?.icon,
            isHovered && classes?.hoverIcon,
            active && classes?.activeIcon,
          )}
        >
          {icon}
        </div>
      )}
      <div
        className={cn(
          'font-inherit flex text-nowrap text-inherit',
          classes?.label,
          isHovered && classes?.hoverLabel,
        )}
        data-testid="dropdown-menu-item-label"
      >
        {label}
      </div>
      {addon && (
        <BadgeAddOn
          className={cn('mx-2 text-nowrap', !rightIcon && 'mr-[35px]', classes?.addon)}
          data-pendo={addon}
          data-testid="dropdown-menu-item-addon"
        />
      )}
      {rightIcon && (
        <div
          data-testid="dropdown-menu-item-right-icon"
          className={cn(classes?.rightIcon, isHovered && classes?.hoverRightIcon)}
        >
          {rightIcon}
        </div>
      )}
    </Component>
  )
}

export default DropdownMenuItem
